import { format } from 'date-fns';
import _ from 'lodash';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    mdiCircleOffOutline,
    mdiSpeedometer,
    mdiTrendingUp,
    mdiLightningBolt,
    mdiAlertCircleOutline,
    mdiTag,
    mdiAccountMultiple,
} from '@mdi/js';
import { BaseSelect } from '../../base/select';
import { DateIconSelect } from '../../base/date-icon-select';
import { IconSelect, IIconSelectOption } from '../../base/icon-select';
import * as S from './message-fields.styles';
import { CoreFetch } from '../../../api';
import { UserApi, TagApi, MessageApi } from '../../../api/server';
import { MessagingContext } from '../../../services';
import { Swatches } from '../../base/variables/variables';
import { Variables } from '../../base/variables/variables';

export interface IMessageFieldsValues {
    priority?: string;
    dueDate?: string; // Format: yyyy-MM-dd
    categoryIds?: string[];
    assignedUserIds?: string[];
}

interface IMessageFieldsProps {
    values: IMessageFieldsValues;
    onChange: (values: IMessageFieldsValues) => void;
    isCategorySaving?: boolean;
    isAssignedUsersSaving?: boolean;
}

// Priority options with icons and colors
const priorityOptions: IIconSelectOption[] = [
    {
        value: undefined,
        label: 'No priority',
        icon: mdiCircleOffOutline,
        iconColor: Variables.Colors.n15,
    },
    {
        value: 'LOW',
        label: 'Low',
        icon: mdiSpeedometer,
        iconColor: Swatches.AlphaMiddleGreen80,
    },
    {
        value: 'NORMAL',
        label: 'Normal',
        icon: mdiTrendingUp,
        iconColor: Swatches.AlphaMiddleBlue80,
    },
    {
        value: 'HIGH',
        label: 'High',
        icon: mdiLightningBolt,
        iconColor: Swatches.AlphaMiddleOrange80,
    },
    {
        value: 'URGENT',
        label: 'Urgent',
        icon: mdiAlertCircleOutline,
        iconColor: Swatches.AlphaMiddleRed80,
    },
];

export const MessageFields: React.FunctionComponent<IMessageFieldsProps> = (props: IMessageFieldsProps): React.ReactElement => {
    const { values, onChange, isCategorySaving, isAssignedUsersSaving } = props;
    const context = useContext(MessagingContext);
    const isSupportApp = context.account?.supportGrant === true;
    
    // Check if user has CODING_ACCESS role
    const currentAccount = context.account?.accountAccess?.find(
        acc => acc.accountId === context.account?.grantedAccountId
    );
    const isCodingAccessOnly = currentAccount?.roleId === 'CODING_ACCESS';
    
    const [categoryOptions, setCategoryOptions] = useState<Array<{ value: string; label: string }>>([]);
    const [userOptions, setUserOptions] = useState<Array<{ value: string; label: string }>>([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const categories = isSupportApp
                    ? await CoreFetch(MessageApi).listSupportMessageCategory()
                    : await CoreFetch(MessageApi).listProviderMessageCategory();
                
                const categoryOpts = _.map(categories, category => ({
                    value: category.id || '',
                    label: category.name || ''
                }));
                
                setCategoryOptions(categoryOpts);
            } catch (error) {
                console.error('Failed to load message categories:', error);
            }

            // Skip user query if user has CODING_ACCESS role
            if (!isCodingAccessOnly) {
                try {
                    const users = await CoreFetch(UserApi).listProviderUsers({});
                    
                    const userOpts = _.map(users, user => ({
                        value: user.personalAccountId || '',
                        label: `${user.first || ''} ${user.last || ''}`.trim() || user.email || 'Unknown User'
                    }));
                    setUserOptions(userOpts);
                } catch (error) {
                    console.error('Failed to load users:', error);
                }
            }
        };

        fetchOptions();
    }, [isSupportApp, isCodingAccessOnly]);

    const handlePriorityChange = (newValue: string | undefined) => {
        onChange({ ...values, priority: newValue });
    };

    const handleDueDateChange = (dateString: string | undefined) => {
        onChange({ ...values, dueDate: dateString });
    };

    const handleCategoryChange = (newValues: string[]) => {
        onChange({ ...values, categoryIds: newValues });
    };

    const handleAssignedUserChange = (newValues: string[]) => {
        onChange({ ...values, assignedUserIds: newValues });
    };

    // Convert options to IconSelect format (no icons)
    const categoryIconOptions: IIconSelectOption[] = categoryOptions.map(opt => ({
        value: opt.value,
        label: opt.label,
    }));

    const userIconOptions: IIconSelectOption[] = userOptions.map(opt => ({
        value: opt.value,
        label: opt.label,
    }));

    return (
        <S.FieldRow>
            <S.FieldColumnAuto>
                <IconSelect
                    options={priorityOptions}
                    value={values.priority}
                    onChange={handlePriorityChange}
                    disabled={false}
                    tooltip="Priority"
                />
            </S.FieldColumnAuto>
            <S.FieldColumnAuto>
                <IconSelect
                    multiple
                    options={categoryIconOptions}
                    selectedValues={values.categoryIds || []}
                    onMultiChange={handleCategoryChange}
                    buttonIcon={mdiTag}
                    buttonIconColor={Variables.Colors.n70}
                    disabled={isCategorySaving || false}
                    loading={categoryOptions.length === 0 || isCategorySaving || false}
                    tooltip="Category"
                />
            </S.FieldColumnAuto>
            <S.FieldColumnAuto>
                <IconSelect
                    multiple
                    options={userIconOptions}
                    selectedValues={values.assignedUserIds || []}
                    onMultiChange={handleAssignedUserChange}
                    buttonIcon={mdiAccountMultiple}
                    buttonIconColor={Variables.Colors.n70}
                    disabled={isCodingAccessOnly || isAssignedUsersSaving || false}
                    loading={(userOptions.length === 0 && !isCodingAccessOnly) || isAssignedUsersSaving || false}
                    tooltip="Assign to"
                />
            </S.FieldColumnAuto>
            <S.FieldColumnAuto>
                <DateIconSelect
                    value={values.dueDate}
                    onChange={handleDueDateChange}
                    disabled={false}
                    tooltip="Due date"
                />
            </S.FieldColumnAuto>
        </S.FieldRow>
    );
};

